import React, { useEffect, useMemo, useState } from 'react';
import { Input, Select, Modal, Switch, Spin } from 'antd';
import { SearchOutlined, WhatsAppOutlined, CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import useRefLists from '../rapports/useRefLists';
import usePersistentState from '../../hooks/usePersistentState';
import { composerSelection } from './photoComposer';
import partagerPhotos, { telechargerPhotos } from './sharePhotos';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeImage,
  NdoeEmpty, NdoeLoading, NdoeError,
} from '../../components/ndoe';
import './partageCatalogue.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const POINTURES = Array.from({ length: 13 }, (_, i) => 34 + i);
const MAX_PHOTOS = 20;

// Catalogue de partage (M5bis) — mandat « partage WhatsApp » :
// filtres → sélection jusqu'à 20 photos → bande blanche incrustée (pointures
// disponibles / prix / référence, au choix, mémorisé) → feuille de partage
// (WhatsApp Business). UNE PHOTO = UN MODÈLE dans UNE COULEUR, ses pointures
// réellement disponibles agrégées dessus (décision proprio du 11/07).
//
// Le prix affiché/incrusté est le prix de vente CLIENTE : visible pour tous
// les rôles qui ont ce menu (entorse assumée à la garde finance — validée).
const PartageCatalogue = () => {
  const refs = useRefLists(true);
  // Multi-sélection (18/07) : chaque filtre est une LISTE — filtrer P42+P44+P37,
  // plusieurs marques, etc. dans un même critère. Le serveur reçoit les valeurs
  // en « 42,44,37 » et fait un IN (…) (rétrocompatible : une seule valeur = l'ancien =).
  const [filters, setFilters] = useState({ id_categorie: [], id_marque: [], id_couleur: [], taille: [] });
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [selection, setSelection] = useState({});      // key -> modele
  const [infos, setInfos] = usePersistentState('partage_infos_photo', { pointures: true, prix: true, ref: true });
  const [apercu, setApercu] = useState(null);          // { enCours, photos, echecs }
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [telechargeEnCours, setTelechargeEnCours] = useState(false);
  // Chantier #4 : par défaut on ne montre que le disponible ; « Voir tout » révèle
  // aussi les ruptures/indispo (dispo omis côté serveur + pas de filtre client).
  const [voirTout, setVoirTout] = useState(false);
  // Photos « mannequin » (chantier 15/07) : la photo par défaut du modèle
  // (img_partage) prime sur la photo catalogue. `choixPhoto` mémorise une
  // bascule manuelle par carte (cycle mannequin(s) → catalogue) ;
  // `photosParModele` cache la liste des 5 photos (chargée au premier clic).
  const [choixPhoto, setChoixPhoto] = useState({});          // cle -> chemin choisi
  const [photosParModele, setPhotosParModele] = useState({}); // id_produit -> [chemins]
  // Tri décisionnel (chantier catalogue) : '' = A→Z ; sinon meilleures ventes /
  // dernières ventes / derniers arrivages (calculé côté serveur).
  const [tri, setTri] = useState('');

  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      setLoading(true);
      setErreur(null);
      try {
        const p = new URLSearchParams({ limit: '600' });
        if (!voirTout) p.set('dispo', 'disponible');
        if (tri) p.set('sort', tri);
        if (q.trim()) p.set('q', q.trim());
        // Chaque filtre est une liste : on l'envoie en « 42,44,37 » (le serveur
        // fait un IN). Liste vide → paramètre omis, donc pas de filtre.
        Object.entries(filters).forEach(([k, v]) => {
          if (Array.isArray(v) ? v.length : v) p.set(k, [].concat(v).join(','));
        });
        const { data } = await axios.get(`${DOMAIN}/api/catalogue/stock?${p.toString()}`);
        if (alive) setRows(Array.isArray(data) ? data : (data?.variantes || []));
      } catch (e) {
        if (alive) setErreur(e.response?.data?.error || e.message);
      } finally { if (alive) setLoading(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, filters, voirTout, tri]);

  // antd en mode multiple renvoie déjà un tableau (vide si on efface tout).
  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v || [] }));

  // Regroupement MODÈLE + COULEUR : pointures disponibles agrégées, prix en
  // fourchette si les tailles n'ont pas le même prix.
  const modeles = useMemo(() => {
    const parCle = {};
    rows.forEach((v) => {
      if (!voirTout && !(v.disponible > 0)) return;
      const cle = `${v.id_produit}|${v.couleur || ''}`;
      const g = parCle[cle] || (parCle[cle] = {
        cle,
        id_produit: v.id_produit,
        nom_produit: v.nom_produit,
        code_produit: v.code_produit,
        code_article: v.code_article,
        marque: v.marque,
        couleur: v.couleur,
        img: v.img,
        img_partage: v.img_partage || null,
        tailles: [],
        prixMin: Infinity,
        prixMax: 0,
        dispoTotal: 0,
      });
      if (!g.img && v.img) g.img = v.img;
      if (!g.img_partage && v.img_partage) g.img_partage = v.img_partage;
      // On n'agrège que les pointures RÉELLEMENT disponibles (même en « Voir tout ») :
      // la bande blanche WhatsApp les présente comme « pointures disponibles »
      // (invariant en tête de fichier) — jamais incruster une pointure en rupture.
      if (v.taille != null && v.disponible > 0 && !g.tailles.includes(v.taille)) g.tailles.push(v.taille);
      const prix = Number(v.prix);
      if (Number.isFinite(prix) && prix > 0) {
        g.prixMin = Math.min(g.prixMin, prix);
        g.prixMax = Math.max(g.prixMax, prix);
      }
      // Prix promotionnel (chantier 15/07) : le meilleur prix promo du groupe.
      if (v.prix_promo != null) {
        g.promoMin = Math.min(g.promoMin ?? Infinity, Number(v.prix_promo));
        g.promoLibelle = g.promoLibelle || v.promo_libelle || null;
      }
      g.dispoTotal += Number(v.disponible) || 0;
    });
    return Object.values(parCle).map((g) => ({
      ...g,
      tailles: g.tailles.sort((a, b) => Number(a) - Number(b)),
      // Bandeau incrusté : quand une promo est active, le prix cliente devient
      // « promo $ au lieu de base $ » (décision Q5 validée) — sinon inchangé.
      prixTexte: g.promoMin != null && g.prixMax > 0
        ? `${g.promoMin} $ au lieu de ${g.prixMin === g.prixMax ? g.prixMin : `${g.prixMin}–${g.prixMax}`} $`
        : g.prixMax > 0
          ? (g.prixMin === g.prixMax ? `${g.prixMin} $` : `${g.prixMin}–${g.prixMax} $`)
          : null,
    }));
  }, [rows, voirTout]);

  const nb = Object.keys(selection).length;

  const basculer = (m) => {
    setSelection((sel) => {
      if (sel[m.cle]) {
        const { [m.cle]: _out, ...reste } = sel;
        return reste;
      }
      if (Object.keys(sel).length >= MAX_PHOTOS) {
        toast.info(`Maximum ${MAX_PHOTOS} photos par partage.`);
        return sel;
      }
      return { ...sel, [m.cle]: m };
    });
  };

  // Photo réellement utilisée pour une carte : bascule manuelle > photo
  // mannequin par défaut > photo catalogue.
  const imgEffective = (m) => choixPhoto[m.cle] || m.img_partage || m.img;

  // Bascule la photo d'une carte SÉLECTIONNÉE : cycle photo(s) mannequin puis
  // catalogue. La liste des 5 est chargée au premier clic (puis mise en cache).
  const basculerPhoto = async (m, e) => {
    e.stopPropagation();
    let liste = photosParModele[m.id_produit];
    if (!liste) {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/photos-partage/${m.id_produit}`);
        liste = (data?.photos || []).map((p) => p.chemin);
      } catch (err) { liste = m.img_partage ? [m.img_partage] : []; }
      setPhotosParModele((s) => ({ ...s, [m.id_produit]: liste }));
    }
    const options = [...liste, m.img].filter(Boolean);
    if (options.length < 2) return;
    const courante = imgEffective(m);
    const suivante = options[(options.indexOf(courante) + 1) % options.length];
    setChoixPhoto((s) => ({ ...s, [m.cle]: suivante }));
  };

  const ouvrirApercu = async () => {
    // La composition lit `modele.img` : on injecte la photo effective
    // (mannequin par défaut ou bascule manuelle) sans toucher au composeur.
    const choisis = Object.values(selection).map((m) => ({ ...m, img: imgEffective(m) }));
    if (!choisis.length) return;
    setApercu({ enCours: true, photos: [], echecs: [] });
    const { ok, echecs } = await composerSelection(choisis, infos);
    setApercu({ enCours: false, photos: ok, echecs });
    if (echecs.length) toast.warn(`${echecs.length} photo(s) sans image exploitable — exclue(s) du partage.`);
  };

  const envoyer = async () => {
    if (!apercu?.photos?.length || envoiEnCours) return;
    setEnvoiEnCours(true);
    try {
      const canal = await partagerPhotos(apercu.photos);
      if (canal === 'telechargement') {
        toast.success(`${apercu.photos.length} photo(s) téléchargée(s) — glissez-les dans WhatsApp Web.`);
      } else {
        toast.success('Feuille de partage ouverte — choisissez WhatsApp Business.');
      }
      setApercu(null);
    } catch (e) {
      // L'utilisateur a fermé la feuille de partage : pas une erreur.
      if (e?.name !== 'AbortError' && !/cancel/i.test(e?.message || '')) {
        toast.error(e?.message || 'Partage impossible.');
      }
    } finally { setEnvoiEnCours(false); }
  };

  const telecharger = async () => {
    if (!apercu?.photos?.length || telechargeEnCours) return;
    setTelechargeEnCours(true);
    try {
      const { canal, dossier } = await telechargerPhotos(apercu.photos);
      const n = apercu.photos.length;
      if (canal === 'appareil') {
        toast.success(`${n} photo(s) enregistrée(s) dans « ${dossier} » sur votre téléphone.`);
      } else {
        toast.success(`${n} photo(s) téléchargée(s).`);
      }
      setApercu(null);
    } catch (e) {
      toast.error(e?.message || 'Téléchargement impossible.');
    } finally { setTelechargeEnCours(false); }
  };

  return (
    <NdoePage className="pc-page">
      <NdoePageHeader
        eyebrow="Catalogue"
        title="Catalogue de partage"
        subtitle="Filtrez, choisissez jusqu'à 20 paires, puis partagez les photos (avec pointures, prix, référence) sur WhatsApp Business."
      />

      <div className="pc-filtres">
        <Input allowClear prefix={<SearchOutlined />} placeholder="Nom ou référence (ex. ESC-MNG)…"
          value={q} onChange={(e) => setQ(e.target.value)} className="pc-f pc-f-q" />
        {/* mode="multiple" : plusieurs valeurs par critère (ex. P42+P44+P37).
            maxTagCount="responsive" évite que les étiquettes débordent du champ
            (elles se replient en « +N »). */}
        <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="Catégorie" options={refs.categories} value={filters.id_categorie} onChange={setF('id_categorie')} showSearch optionFilterProp="label" className="pc-f" />
        <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="Marque" options={refs.marques} value={filters.id_marque} onChange={setF('id_marque')} showSearch optionFilterProp="label" className="pc-f" />
        <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="Couleur" options={refs.couleurs} value={filters.id_couleur} onChange={setF('id_couleur')} showSearch optionFilterProp="label" className="pc-f" />
        <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="Pointure" options={POINTURES.map((p) => ({ value: p, label: `P${p}` }))} value={filters.taille} onChange={setF('taille')} className="pc-f pc-f-pt" />
        <Select value={tri} onChange={setTri} className="pc-f pc-f-tri" title="Trier le catalogue"
          options={[
            { value: '', label: 'Tri : A→Z' },
            { value: 'sales_desc', label: 'Meilleures ventes (90 j)' },
            { value: 'dormant_stock', label: 'Dort en stock (0 vente 90 j)' },
            { value: 'never_sold', label: 'Jamais vendu' },
            { value: 'restock', label: 'À réassortir (se vend, stock bas)' },
            { value: 'last_sale', label: 'Dernières ventes' },
            { value: 'last_receipt', label: 'Derniers arrivages' },
          ]} />
        <label className="pc-f-stock" title="Par défaut, seules les paires en stock sont affichées">
          <Switch size="small" checked={voirTout} onChange={setVoirTout} /> Voir tout (ruptures incluses)
        </label>
      </div>

      <section className="ndoe-card pc-infos">
        <div className="pc-infos-titre">
          <b>Infos incrustées sur la photo</b>
          <small>Mémorisé pour les prochains partages</small>
        </div>
        <div className="pc-infos-switchs">
          <label><Switch size="small" checked={!!infos.pointures} onChange={(v) => setInfos({ ...infos, pointures: v })} /> Pointures disponibles</label>
          <label><Switch size="small" checked={!!infos.prix} onChange={(v) => setInfos({ ...infos, prix: v })} /> Prix</label>
          <label><Switch size="small" checked={!!infos.ref} onChange={(v) => setInfos({ ...infos, ref: v })} /> Référence</label>
        </div>
      </section>

      {loading ? (
        <NdoeLoading rows={4} />
      ) : erreur ? (
        <NdoeError text={erreur} onRetry={() => setQ((s) => s)} />
      ) : !modeles.length ? (
        <NdoeEmpty icon="👟" title="Aucune paire pour ces critères" text={voirTout ? 'Élargissez les filtres.' : 'Aucune paire en stock — activez « Voir tout » pour inclure les ruptures.'} />
      ) : (
        <div className="pc-grille">
          {modeles.map((m) => {
            const choisi = !!selection[m.cle];
            return (
              <button type="button" key={m.cle} className={`pc-carte ${choisi ? 'is-choisie' : ''} ${m.dispoTotal <= 0 ? 'is-rupture' : ''}`} onClick={() => basculer(m)}>
                <span className="pc-coche" aria-hidden="true">{choisi ? '✓' : ''}</span>
                {m.dispoTotal <= 0 && <span className="pc-badge-rupture">Rupture</span>}
                {imgEffective(m) !== m.img && <span className="pc-badge-mannequin">📸 mannequin</span>}
                {/* Grille fluide (cellules ~140-220 px) : .pc-thumb force la
                    largeur à 100 % — `size` ne dit donc PAS la taille réelle.
                    On passe `sizes` pour que le navigateur choisisse la vignette
                    d'après la largeur rendue et la densité de l'écran. */}
                <div className="pc-thumb"><NdoeImage src={imgEffective(m)} alt={m.nom_produit || 'Article'} size="lg" sizes="200px" preview={false} /></div>
                {choisi && (m.img_partage || choixPhoto[m.cle]) && (
                  <span role="button" tabIndex={0} className="pc-basculer-photo" title="Changer la photo utilisée (mannequin / catalogue)"
                    onClick={(e) => basculerPhoto(m, e)}
                    onKeyDown={(e) => { if (e.key === 'Enter') basculerPhoto(m, e); }}>
                    ⇆ photo
                  </span>
                )}
                <span className="pc-ref num">{m.code_produit || m.code_article || '—'}</span>
                <span className="pc-nom">{m.nom_produit}</span>
                <span className="pc-sub">{[m.marque, m.couleur].filter(Boolean).join(' · ')}</span>
                {m.tailles.length > 0 && <span className="pc-tailles num">P{m.tailles.join(' · ')}</span>}
                {m.promoMin != null
                  ? <span className="pc-prix num"><s>{m.prixMin === m.prixMax ? `${m.prixMin} $` : `${m.prixMin}–${m.prixMax} $`}</s> <b className="pc-prix-promo">{m.promoMin} $</b></span>
                  : m.prixTexte && <span className="pc-prix num">{m.prixTexte}</span>}
                {m.promoMin != null && <span className="pc-badge-promo">PROMO</span>}
              </button>
            );
          })}
        </div>
      )}

      {nb > 0 && (
        <div className="pc-barre">
          <span className="num"><b>{nb}</b>/{MAX_PHOTOS} photo(s)</span>
          <div className="pc-barre-actions">
            <NdoeActionButton size="sm" icon={<CloseOutlined />} onClick={() => setSelection({})}>Vider</NdoeActionButton>
            <NdoeActionButton variant="primary" icon={<WhatsAppOutlined />} onClick={ouvrirApercu}>
              Aperçu & partager
            </NdoeActionButton>
          </div>
        </div>
      )}

      <Modal
        open={!!apercu}
        onCancel={() => setApercu(null)}
        footer={null}
        width={760}
        title={`Aperçu — ${apercu?.photos?.length ?? 0} photo(s) prête(s)`}
        className="pc-modal"
        destroyOnClose
      >
        {apercu?.enCours ? (
          <div className="pc-compose"><Spin /> <span>Composition des photos…</span></div>
        ) : (
          <>
            <div className="pc-apercu-grille">
              {(apercu?.photos || []).map((p) => (
                <img key={p.fileName} src={p.dataUrl} alt={p.fileName} className="pc-apercu-img" />
              ))}
            </div>
            {/* Deux choix (demande proprio 18/07) : enregistrer les photos en
                local, OU ouvrir la feuille de partage WhatsApp. */}
            <div className="pc-apercu-actions">
              <NdoeActionButton block loading={telechargeEnCours} icon={<DownloadOutlined />} onClick={telecharger}>
                Télécharger les photos
              </NdoeActionButton>
              <NdoeActionButton variant="primary" block loading={envoiEnCours} icon={<WhatsAppOutlined />} onClick={envoyer}>
                Partager sur WhatsApp Business
              </NdoeActionButton>
            </div>
            <p className="pc-note">
              <b>Télécharger</b> : les photos sont enregistrées sur votre téléphone (dossier « Documents/Ndoe ») ou votre ordinateur.<br />
              <b>Partager</b> : la feuille WhatsApp s'ouvre — choisissez WhatsApp Business puis la conversation.
              Sur ordinateur, les photos sont téléchargées, prêtes à glisser dans WhatsApp Web.
            </p>
          </>
        )}
      </Modal>
    </NdoePage>
  );
};

export default PartageCatalogue;
